'use client';

import { Button, ButtonDropdown, LayoutContainer, Toolbar } from '@petsmart-ui/sparky';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    ClearRefinements,
    useClearRefinements,
    useCurrentRefinements,
    useInstantSearch,
    useRange,
    useRefinementList,
    useSortBy,
    useStats,
} from 'react-instantsearch';

import {
    AlgoliaIndice,
    countFilters,
    FILTERS,
    getRangeTitle,
    getSortLabelFromIndex,
    isEligibleValue,
    normalizeDynamicFacetOrder,
    SORT_INDEX_PLP,
    SORT_INDEX_SEARCH,
    totalFilters,
} from '@libs/algolia';
import { trackAutoshipQuickFilter } from '@libs/analytics';
import {
    clsx,
    useDecisions,
    useDeviceType,
    useFeaturedBrand,
    useOptimizely,
    usePageLayout,
    useSearchPage,
} from '@libs/common-next';

import FilterByRating from '@/components/FilterByRating/FilterByRating';
import FilterRefinementList from '@/components/FilterRefinementList';
import FindStoreNearMe from '@/components/FindStoreNearMe';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import PriceRange from '@/components/PriceRange';
import { CATEGORIES_ATTRIBUTE } from '@/components/SearchLayout/utils';
import SortProduct from '@/components/SortProduct/SortProduct';
import { usePriceRangeContext } from '@/providers/PriceRangeProvider';

import OptexDeliveryFilterExperiment from '../OptexDeliveryFilterExperiment';

import '@/components/FilterContainer/FilterContainer.scss';

const DrawerFilter = dynamic(() => import('@/components/DrawerFilter'), {
    ssr: true,
    loading: () => <LoadingOverlay show />,
});

const handlerSize: Record<string, object> = {
    mobile: {
        behavior: 'overflow-sm',
        className: 'filter-wrapper filter-wrapper--mobile',
    },
    tablet: {
        className: 'filter-wrapper filter-wrapper--tablet',
    },
    desktop: {
        className: 'filter-wrapper filter-wrapper--desktop',
    },
};

type FilterContainerProps = {
    pageId: string;
    maxRateAvailable: number;
};

export default function FilterContainer({ pageId, maxRateAvailable }: FilterContainerProps) {
    const isFeatured = useFeaturedBrand();
    const isAutoshipQuickFilterEnabled = useDecisions('x_as_quickfilter');
    const { x_deli_filter, browse_drr } = useDecisions();
    const { context } = useOptimizely();
    const { storeDrawerOpen, setStoreDrawerOpen } = usePageLayout();
    const isSearchPage = useSearchPage();
    const sortby = useSortBy(isSearchPage ? SORT_INDEX_SEARCH : SORT_INDEX_PLP);

    const { minValue, maxValue, clearRange } = usePriceRangeContext();
    const { refine: clearShop } = useClearRefinements({
        includedAttributes: [FILTERS.SHOP_BY_STORE.ATTRIBUTE, FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE],
    });

    const priceRange = useRange({ attribute: FILTERS.RANGE.ATTRIBUTE });
    const excludedAttributes = [CATEGORIES_ATTRIBUTE, 'query'];
    if (isFeatured) {
        excludedAttributes.push(FILTERS.BRAND.ATTRIBUTE);
    }
    const { items } = useCurrentRefinements({
        excludedAttributes,
    });

    const hasIsBopisEligibleFilter = items.some(filter => filter.attribute === FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE);

    const [isActive, setIsActive] = useState<boolean>(false);
    const [orderLabel, setOrderLabel] = useState<string>(getSortLabelFromIndex(sortby.currentRefinement) ?? '');
    const [isStoreFilterApplied, setIsStoreFilterApplied] = useState<boolean>(false);
    const { deviceType, isDesktop } = useDeviceType();
    const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);

    const handleDrawer = () => {
        setIsActive(!isActive);
    };

    function handleBrandDropdownState(value: boolean): void {
        setIsBrandDropdownOpen(value);
    }

    const total = totalFilters(items) - (hasIsBopisEligibleFilter ? 1 : 0);
    const counters = countFilters(items);

    const counterFilterTitle = () => (total > 0 ? `(${total})` : '');

    const isMobile = () => !isDesktop;

    const behaviorResponsive = !isMobile() ? undefined : 'responsive';

    const buttonDropdownClasses = clsx(
        'filter-container__button-dropdown list-filter',
        Boolean(total) && 'all-filters'
    );

    const sortProducts = () => {
        sortby.refine(isSearchPage || browse_drr ? AlgoliaIndice.PRODUCTS : AlgoliaIndice.PRODUCTS_BEST_SELLERS);
    };

    const { results: instantSearchResults, status } = useInstantSearch();

    const { nbHits } = useStats();
    const dynamicFacets = normalizeDynamicFacetOrder(
        instantSearchResults?.renderingContent?.facetOrdering?.facets?.order ?? []
    );

    const autoshipRefinements = useRefinementList({
        attribute: FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE,
    });
    const autoshipFacet = autoshipRefinements.items.find(item => isEligibleValue(item.value));
    const hasAutoshipEligibleProducts = Boolean(autoshipFacet?.count);
    const isAutoshipFilterApplied = Boolean(autoshipFacet?.isRefined);
    const dynamicFacetsForDrawer = dynamicFacets.filter(facet => facet !== FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE);

    const pendingAutoshipAnalytics = useRef(false);

    const toggleAutoshipFilter = () => {
        if (!autoshipFacet) return;
        pendingAutoshipAnalytics.current = !isAutoshipFilterApplied;
        autoshipRefinements.refine(autoshipFacet.value);
    };

    const handleOptexFilterEngagement = () => {
        context?.trackEvent?.('filter_engagement');
    };

    useEffect(() => {
        if (!isAutoshipFilterApplied) {
            pendingAutoshipAnalytics.current = false;
        }
    }, [isAutoshipFilterApplied]);

    useEffect(() => {
        if (!pendingAutoshipAnalytics.current) {
            return;
        }

        if (status === 'error') {
            pendingAutoshipAnalytics.current = false;
            return;
        }

        if (status === 'idle' && isAutoshipFilterApplied) {
            trackAutoshipQuickFilter(nbHits);
            pendingAutoshipAnalytics.current = false;
        }
    }, [isAutoshipFilterApplied, nbHits, status]);

    const onOpenStoreModal = useCallback(() => setStoreDrawerOpen({ pageType: 'plp', showStoreDetails: true }), []);

    useEffect(() => {
        setOrderLabel(getSortLabelFromIndex(sortby.currentRefinement) ?? orderLabel);
    }, [orderLabel, sortby.currentRefinement]);

    const hasStoreFilter = items.some(item => item.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE);
    const hasBrandFilter = items.some(item => item.attribute === FILTERS.BRAND.ATTRIBUTE);
    const hasRangeFilter = items.some(item => item.attribute === FILTERS.RANGE.ATTRIBUTE);
    const hasRatingFilter = items.some(item => item.attribute === FILTERS.RATING.ATTRIBUTE);

    const clearFiltersButtonClassName = 'drawer-filter__button-clear-filters';
    const isDrawerCreatedRef = useRef(false);
    if (isActive && !storeDrawerOpen) {
        isDrawerCreatedRef.current = true;
    }

    const renderDrawer = isDrawerCreatedRef.current ? (
        <DrawerFilter
            pageId={pageId}
            clearShop={clearShop}
            isActive={isActive && !storeDrawerOpen}
            isMobileVersion={isMobile()}
            handlerDrawer={handleDrawer}
            label={FILTERS.DRAWER.LABEL}
            description={FILTERS.DRAWER.DESCRIPTION}
            sort={sortby}
            priceRange={priceRange}
            openStoreModal={onOpenStoreModal}
            dynamicFacets={dynamicFacetsForDrawer}
            hasAutoshipEligibleProducts={hasAutoshipEligibleProducts}
            sortProducts={sortProducts}
            maxRateAvailable={maxRateAvailable}
            refinements={[]}
            className="filter-container__drawer-filter"
        />
    ) : null;

    return (
        <LayoutContainer {...handlerSize[deviceType]}>
            <div
                className={cn('filter-container', isSearchPage && 'filter-container--search')}
                data-testid="filter-container"
                data-search-interaction="true"
            >
                <Toolbar>
                    <Toolbar.Item>
                        <Button
                            variant="toolbar"
                            iconName="filter"
                            iconSize="sm"
                            onClick={handleDrawer}
                            data-testid="all-filters-button"
                            text={`${
                                isMobile() ? FILTERS.HEADER_TITLE.MOBILE : FILTERS.HEADER_TITLE.DESKTOP
                            } ${counterFilterTitle()}`}
                            className={buttonDropdownClasses}
                        />
                    </Toolbar.Item>
                    <Toolbar.Item>
                        {/* Optimizely START : DTO-395 */}
                        {x_deli_filter ? (
                            <OptexDeliveryFilterExperiment
                                behavior={behaviorResponsive}
                                pageId={pageId}
                                openStoreModal={onOpenStoreModal}
                                clearShop={clearShop}
                                handleIsStoreFilterApplied={setIsStoreFilterApplied}
                            />
                        ) : (
                            <ButtonDropdown
                                behavior={behaviorResponsive}
                                variant="toolbar"
                                buttonText={FILTERS.SHOP_BY_STORE.TITLE}
                                ariaLabelledBy={FILTERS.SHOP_BY_STORE.TITLE}
                                drawerTitle={FILTERS.SHOP_BY_STORE.TITLE}
                                className={`filter-container__button-dropdown store-filter ${
                                    isStoreFilterApplied ? 'all-filters' : ''
                                }`}
                                onStateChange={(isActive: boolean) => {
                                    if (isActive) {
                                        handleOptexFilterEngagement();
                                    }
                                }}
                                data-testid="filter-button-pickup-delivery"
                                titleAfter={
                                    hasStoreFilter && (
                                        <ClearRefinements
                                            includedAttributes={[
                                                FILTERS.SHOP_BY_STORE.ATTRIBUTE,
                                                FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE,
                                            ]}
                                            title={FILTERS.SHOP_BY_STORE.TITLE}
                                            translations={{
                                                resetButtonText: FILTERS.CLEAR.TITLE,
                                            }}
                                            classNames={{
                                                button: clearFiltersButtonClassName,
                                            }}
                                        />
                                    )
                                }
                            >
                                <div className="filter-drawer-container">
                                    <FindStoreNearMe
                                        pageId={pageId}
                                        openStoreModal={onOpenStoreModal}
                                        clearShop={clearShop}
                                        showActions
                                        handleIsStoreFilterApplied={setIsStoreFilterApplied}
                                    />
                                </div>
                            </ButtonDropdown>
                        )}
                        {/* Optimizely END : DTO-395 */}
                    </Toolbar.Item>
                    {!isFeatured && (
                        <Toolbar.Item>
                            <ButtonDropdown
                                behavior={behaviorResponsive}
                                variant="toolbar"
                                buttonText={`${FILTERS.BRAND.TITLE} ${counters(FILTERS.BRAND.ATTRIBUTE)}`}
                                ariaLabelledBy={FILTERS.BRAND.TITLE}
                                drawerTitle={FILTERS.BRAND.TITLE}
                                className="filter-container__button-dropdown brand-filter"
                                data-testid="filter-refinement-button-brand"
                                onChange={() => null}
                                titleAfter={
                                    hasBrandFilter && (
                                        <ClearRefinements
                                            includedAttributes={[FILTERS.BRAND.ATTRIBUTE]}
                                            title={FILTERS.CLEAR.TITLE}
                                            translations={{
                                                resetButtonText: FILTERS.CLEAR.TITLE,
                                            }}
                                            classNames={{
                                                button: clearFiltersButtonClassName,
                                            }}
                                            data-testid="filter-refinement-list-brand-button-clear"
                                        />
                                    )
                                }
                                onStateChange={handleBrandDropdownState}
                            >
                                <div className="filter-drawer-container">
                                    <FilterRefinementList
                                        pageId={pageId}
                                        attribute={FILTERS.BRAND.ATTRIBUTE}
                                        showActions
                                        limit={FILTERS.BRAND.SHOW_MORE_LIMIT}
                                        placeholder={FILTERS.BRAND.PLACEHOLDER}
                                        isDropdownOpen={isBrandDropdownOpen}
                                    />
                                </div>
                            </ButtonDropdown>
                        </Toolbar.Item>
                    )}
                    <Toolbar.Item>
                        <ButtonDropdown
                            behavior={behaviorResponsive}
                            variant="toolbar"
                            buttonText={getRangeTitle(minValue, maxValue)}
                            ariaLabelledBy={FILTERS.RANGE.TITLE}
                            drawerTitle={FILTERS.RANGE.TITLE}
                            className="filter-container__button-dropdown filter-range"
                            data-testid="filter-by-price"
                            onChange={() => null}
                            titleAfter={
                                hasRangeFilter && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        text={FILTERS.CLEAR.TITLE}
                                        onClick={() => clearRange()}
                                        className={clearFiltersButtonClassName}
                                    />
                                )
                            }
                        >
                            <div className="filter-drawer-container price-tool-bar">
                                <PriceRange pageId={pageId} priceRange={priceRange} showClear showView />
                            </div>
                        </ButtonDropdown>
                    </Toolbar.Item>
                    <Toolbar.Item>
                        <ButtonDropdown
                            behavior={behaviorResponsive}
                            variant="toolbar"
                            buttonText={counters(FILTERS.RATING.ATTRIBUTE, FILTERS.RATING.TITLE)}
                            ariaLabelledBy={FILTERS.RATING.TITLE}
                            drawerTitle={FILTERS.RATING.TITLE}
                            className="filter-container__button-dropdown filter-rating"
                            data-testid="filter-button-rating"
                            onChange={() => null}
                            titleAfter={
                                hasRatingFilter && (
                                    <ClearRefinements
                                        includedAttributes={[FILTERS.RATING.ATTRIBUTE]}
                                        title={FILTERS.CLEAR.TITLE}
                                        translations={{
                                            resetButtonText: FILTERS.CLEAR.TITLE,
                                        }}
                                        classNames={{
                                            button: clearFiltersButtonClassName,
                                        }}
                                        data-testid="filter-refinement-list-rating-button-clear"
                                    />
                                )
                            }
                        >
                            <div className="filter-drawer-container">
                                <FilterByRating
                                    pageId={pageId}
                                    attribute={FILTERS.RATING.ATTRIBUTE}
                                    items={FILTERS.RATING.ITEMS}
                                    showActions
                                />
                            </div>
                        </ButtonDropdown>
                    </Toolbar.Item>
                    {isAutoshipQuickFilterEnabled && hasAutoshipEligibleProducts && (
                        <Toolbar.Item>
                            <Button
                                variant="toolbar"
                                text={FILTERS.SUBSCRIPTION_ENABLED.TITLE}
                                data-testid="filter-button-autoship"
                                className={clsx(
                                    'filter-container__button-autoship',
                                    isAutoshipFilterApplied && 'filter-container__button-autoship--active'
                                )}
                                onClick={toggleAutoshipFilter}
                                aria-pressed={isAutoshipFilterApplied}
                            />
                        </Toolbar.Item>
                    )}
                    {isMobile() ? null : (
                        <Toolbar.Item align="right">
                            <ButtonDropdown
                                behavior={behaviorResponsive}
                                variant="toolbar"
                                buttonText={
                                    <>
                                        {FILTERS.SORT_BY.TITLE}
                                        <span className="selected-text">{` ${orderLabel}`}</span>
                                    </>
                                }
                                ariaLabelledBy={FILTERS.SORT_BY.TITLE}
                                data-testid="filter-button-sort-by"
                                className="filter-container__sorting-product sort-filter"
                                onChange={() => null}
                            >
                                <SortProduct type="check" {...sortby} />
                            </ButtonDropdown>
                        </Toolbar.Item>
                    )}
                </Toolbar>
            </div>
            {renderDrawer}
        </LayoutContainer>
    );
}
